import {FC, PropsWithChildren, useEffect, useState} from 'react'

import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {Modal} from '@/components/common/modal/Modal'
import {FeatureFlags, useDebug} from '@/components/context/Debug'
import {usePreferences} from '@/components/context/Preferences'
import {Switcher} from '@/components/inputs/Switcher'
import {Link} from '@/i18n'
import {DEBUG_MODAL_WIDTH} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {Affiliate, affiliateConfig} from '@/utils/affiliate/config'
import {Domain, Region} from '@/utils/countries'
import {getCountriesAlpha2} from '@/utils/countries/helpers'
import {DebugOverrides, readDebugOverrides, writeDebugOverrides} from '@/utils/preferences/debug'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'

import {useABTestConfig} from '../../context/ABTest'

type DebugSectionProps = {
  title?: string
  description?: string
}

const DebugSection: FC<PropsWithChildren<DebugSectionProps>> = ({title, description, children}) => (
  <>
    <div className="section">
      {(title || description) && (
        <div className="heading">
          {title && (
            <Text variant={TextVariant.h5} element="p" fontWeight={TextFontWeight.bold}>
              {title}
            </Text>
          )}
          {description && (
            <Text variant={TextVariant.text14} color={theme.palette.text.light}>
              {description}
            </Text>
          )}
        </div>
      )}
      {children}
    </div>

    <style jsx>{`
      .section {
        padding: ${theme.spacing[5]};
      }
      .heading {
        display: flex;
        flex-direction: column;
        gap: ${theme.spacing[1]};
        margin-bottom: ${theme.spacing[4]};
      }
      @media ${MOBILE_SCREENSIZE} {
        .section {
          padding: ${theme.spacing[4]};
        }
      }
    `}</style>
  </>
)

const countries = getCountriesAlpha2().sort()

export const DebugModal: FC = () => {
  const {isDebugModalOpen, closeDebugModal, getFlagValue, setFlagValue} = useDebug()
  const {destination, setDestination, affiliate, setAffiliate} = usePreferences()
  const {config, setABTest} = useABTestConfig()
  const [overrides, setOverrides] = useState<DebugOverrides>({})

  useEffect(() => {
    if (isDebugModalOpen) {
      setOverrides(readDebugOverrides())
    }
  }, [isDebugModalOpen])

  // Reloads, so a server-rendered page simulates the same visitor as a static one
  const simulate = (change: DebugOverrides) => {
    writeDebugOverrides({...overrides, ...change})
    location.reload()
  }

  return (
    <>
      <Modal isVisible={isDebugModalOpen} closeModal={closeDebugModal} hasPadding={false}>
        <div className="content">
          <DebugSection title="Debug modal">
            <div className="row">
              <Text>Log analytics events to console</Text>
              <Switcher
                isAllowed={getFlagValue(FeatureFlags.logAnalyticsToConsole)}
                onChange={(value) => setFlagValue(FeatureFlags.logAnalyticsToConsole, value)}
              />
            </div>
            <div className="row">
              <Text>Send analytics events to providers</Text>
              <Switcher
                isAllowed={getFlagValue(FeatureFlags.sendAnalyticsEvents)}
                onChange={(value) => setFlagValue(FeatureFlags.sendAnalyticsEvents, value)}
              />
            </div>
            <div className="row">
              <Text>Destination select</Text>
              <select
                value={destination ?? undefined}
                onChange={(e) => setDestination(e.target.value as Region)}
              >
                {Object.values(Region).map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
            <div className="row">
              <Text>Affiliate select</Text>
              <select
                value={affiliate ?? undefined}
                onChange={(e) => setAffiliate(e.target.value as Affiliate)}
              >
                {[undefined, ...Object.keys(affiliateConfig)].map((r) => (
                  <option key={r ?? ''} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
          </DebugSection>
          <DebugSection
            title="Simulated visitor"
            description="Replaces the visitor's country and host; everything else follows from them"
          >
            <div className="row">
              <Text>Country</Text>
              <select
                value={overrides.country ?? ''}
                onChange={(e) => simulate({country: e.target.value || undefined})}
              >
                <option value="">real</option>
                {countries.map((country) => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </select>
            </div>
            <div className="row">
              <Text>Host</Text>
              <select
                value={overrides.host ?? ''}
                onChange={(e) => simulate({host: e.target.value || undefined})}
              >
                <option value="">real</option>
                {[...Object.values(Domain), 'nonexistentTesting.domain'].map((host) => (
                  <option key={host} value={host}>
                    {host}
                  </option>
                ))}
              </select>
            </div>
          </DebugSection>
          <DebugSection
            title="AB tests"
            description="BEWARE: Setting the value will take full refresh of the page"
          >
            {Object.keys(config).map((testName) => (
              <div key={testName} className="row">
                <Text>{testName} switch: </Text>
                <Switcher
                  onChange={(value) => {
                    setABTest(testName, value ? 1 : 0)
                    location.reload()
                  }}
                  isAllowed={Boolean(config[testName])}
                />
              </div>
            ))}
          </DebugSection>
          {process.env.NEXT_PUBLIC_COMMIT_ID && (
            <DebugSection>
              Build commit ID:
              <Link
                href={`https://github.com/TramaTM/trama/commit/${process.env.NEXT_PUBLIC_COMMIT_ID}`}
              >
                {process.env.NEXT_PUBLIC_COMMIT_ID}
              </Link>
            </DebugSection>
          )}
        </div>
      </Modal>

      <style jsx>{`
        .content {
          min-width: ${DEBUG_MODAL_WIDTH};
        }
        .row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: ${theme.spacing[3]};
        }
        .row + .row {
          margin-top: ${theme.spacing[3]};
        }
        .row select {
          width: 220px;
        }
        @media ${MOBILE_SCREENSIZE} {
          .content {
            min-width: 100%;
          }
          .row select {
            width: 100%;
          }
        }
      `}</style>
    </>
  )
}
